import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import { PageSpinner } from './components/common';
import DispatchPage from './pages/Dispatch/DispatchPage';
import ClientStatementPage from './pages/Clients/ClientStatementPage';
import { settingsAPI } from './services/api';
import { applyFavicon } from './utils/favicon';
import ErrorBoundary from './components/common/ErrorBoundary';
import ApiStatus from './components/common/ApiStatus';

// const applyFavicon = (url: string | null) => {
//   if (!url) return;
//   let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
//   if (!link) {
//     link = document.createElement('link');
//     link.rel = 'icon';
//     document.head.appendChild(link);
//   }
//   link.href = url;
// };

const LoginPage           = lazy(() => import('./pages/Auth/LoginPage'));
const DashboardPage       = lazy(() => import('./pages/Dashboard/DashboardPage'));
const QuotationListPage   = lazy(() => import('./pages/Quotations/QuotationListPage'));
const QuotationCreatePage = lazy(() => import('./pages/Quotations/QuotationCreatePage'));
const QuotationDetailPage = lazy(() => import('./pages/Quotations/QuotationDetailPage'));
const QuotationEditPage   = lazy(() => import('./pages/Quotations/QuotationEditPage'));
const ProductListPage     = lazy(() => import('./pages/Products/ProductListPage'));
const ClientListPage      = lazy(() => import('./pages/Clients/ClientListPage'));
const AdminUsersPage      = lazy(() => import('./pages/Admin/AdminUsersPage'));
const AdminSettingsPage   = lazy(() => import('./pages/Admin/AdminSettingsPage'));
const PaymentReceivedPage = lazy(() => import('./pages/Orders/PaymentReceivedPage'));
const OrdersPage               = lazy(() => import('./pages/Orders/OrdersPage'));
const ReturnsPage              = lazy(() => import('./pages/Returns/ReturnsPage'));
const DirectSalePage           = lazy(() => import('./pages/DirectSales/DirectSalePage'));
const VendorListPage           = lazy(() => import('./pages/Vendors/VendorListPage'));
const WarehousePage            = lazy(() => import('./pages/Warehouses/WarehousePage'));
const PurchaseOrderListPage    = lazy(() => import('./pages/PurchaseOrders/PurchaseOrderListPage'));
const PurchaseOrderCreatePage  = lazy(() => import('./pages/PurchaseOrders/PurchaseOrderCreatePage'));
const PurchaseOrderDetailPage  = lazy(() => import('./pages/PurchaseOrders/PurchaseOrderDetailPage'));
const StockTransferListPage    = lazy(() => import('./pages/StockTransfers/StockTransferListPage'));
const StockTransferCreatePage  = lazy(() => import('./pages/StockTransfers/StockTransferCreatePage'));
const CategoriesPage = lazy(() => import('./pages/Products/CategoriesPage'));
const ReportsHubPage           = lazy(() => import('./pages/Reports/ReportsHubPage'));
const OutstandingReportPage    = lazy(() => import('./pages/Reports/OutstandingReportPage'));
const ProductSalesReportPage   = lazy(() => import('./pages/Reports/ProductSalesReportPage'));
const SalesSummaryReportPage   = lazy(() => import('./pages/Reports/SalesSummaryReportPage'));
const CurrentStockReportPage   = lazy(() => import('./pages/Reports/CurrentStockReportPage'));
const POReportPage             = lazy(() => import('./pages/Reports/POReportPage'));
const SalesByClientReportPage      = lazy(() => import('./pages/Reports/SalesByClientReportPage'));
const PurchasesByVendorReportPage  = lazy(() => import('./pages/Reports/PurchasesByVendorReportPage'));
const ProfitMarginReportPage       = lazy(() => import('./pages/Reports/ProfitMarginReportPage'));
const SalesByCategoryReportPage    = lazy(() => import('./pages/Reports/SalesByCategoryReportPage'));
const StockValuationReportPage     = lazy(() => import('./pages/Reports/StockValuationReportPage'));
const LowStockReportPage           = lazy(() => import('./pages/Reports/LowStockReportPage'));
const TraceabilityReportPage       = lazy(() => import('./pages/Reports/TraceabilityReportPage'));
const AuditLogReportPage           = lazy(() => import('./pages/Reports/AuditLogReportPage'));




const homePath = (role?: string) => (role === 'deo' ? '/quotations' : '/dashboard');

const Guard = ({ children, adminOnly = false, denyDEO = false, manageOnly = false }: { children: React.ReactNode; adminOnly?: boolean; denyDEO?: boolean; manageOnly?: boolean }) => {
  const { user, loading, error, retry } = useAuth();
  if (loading) return <PageSpinner />;
  if (!user && error) return <section role="alert" className="p-5"><p>{error}</p><button type="button" onClick={retry}>Retry connection</button></section>;
  if (!user)                              return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to={homePath(user.role)} replace />;
  if (manageOnly && user.role !== 'admin' && user.role !== 'supervisor') return <Navigate to={homePath(user.role)} replace />;
  if (denyDEO && user.role === 'deo')     return <Navigate to="/quotations" replace />;
  return <>{children}</>;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const home = homePath(user?.role);

  // Favicon fetch — sirf jab user logged in ho
  useEffect(() => {
    if (!user) return;
    settingsAPI.get()
      .then(r => applyFavicon(r.data.data.company.logo.url))
      .catch(() => {});
  }, [user]);

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={home} replace /> : <LoginPage />} />
        <Route element={<Guard><AppLayout /></Guard>}>
          <Route index element={<Navigate to={home} replace />} />
          <Route path="dashboard"             element={<DashboardPage />} />
          <Route path="quotations"            element={<QuotationListPage />} />
          <Route path="quotations/new"        element={<QuotationCreatePage />} />
          <Route path="quotations/:id"        element={<QuotationDetailPage />} />
          <Route path="quotations/:id/edit"   element={<QuotationEditPage />} />
          <Route path="orders"                element={<Guard denyDEO><OrdersPage /></Guard>} />
          <Route path="payments"              element={<Guard denyDEO><PaymentReceivedPage /></Guard>} />
          <Route path="dispatch"              element={<Guard denyDEO><DispatchPage /></Guard>} />
          <Route path="statement"             element={<Guard denyDEO><ClientStatementPage /></Guard>} />
          <Route path="reports"               element={<Guard denyDEO><ReportsHubPage /></Guard>} />
          <Route path="reports/outstanding"   element={<Guard denyDEO><OutstandingReportPage /></Guard>} />
          <Route path="reports/product-sales" element={<Guard denyDEO><ProductSalesReportPage /></Guard>} />
          <Route path="reports/sales-summary" element={<Guard denyDEO><SalesSummaryReportPage /></Guard>} />
          <Route path="reports/current-stock" element={<Guard denyDEO><CurrentStockReportPage /></Guard>} />
          <Route path="reports/po"            element={<Guard denyDEO><POReportPage /></Guard>} />
          <Route path="reports/sales-by-client"     element={<Guard denyDEO><SalesByClientReportPage /></Guard>} />
          <Route path="reports/purchases-by-vendor" element={<Guard denyDEO><PurchasesByVendorReportPage /></Guard>} />
          <Route path="reports/profit-margin"   element={<Guard denyDEO><ProfitMarginReportPage /></Guard>} />
          <Route path="reports/sales-by-category" element={<Guard denyDEO><SalesByCategoryReportPage /></Guard>} />
          <Route path="reports/stock-valuation" element={<Guard denyDEO><StockValuationReportPage /></Guard>} />
          <Route path="reports/low-stock"       element={<Guard denyDEO><LowStockReportPage /></Guard>} />
          <Route path="reports/traceability"    element={<Guard denyDEO><TraceabilityReportPage /></Guard>} />
          <Route path="reports/audit-log"       element={<Guard adminOnly><AuditLogReportPage /></Guard>} />



          <Route path="returns"                        element={<Guard denyDEO><ReturnsPage /></Guard>} />
          <Route path="direct-sales"                   element={<DirectSalePage />} />
          <Route path="vendors"                        element={<Guard denyDEO><VendorListPage /></Guard>} />
          <Route path="warehouses"                     element={<Guard denyDEO><WarehousePage /></Guard>} />
          <Route path="categories" element={<Guard manageOnly><CategoriesPage /></Guard>} />
          <Route path="purchase-orders"                element={<Guard denyDEO><PurchaseOrderListPage /></Guard>} />
          <Route path="purchase-orders/new"            element={<Guard denyDEO><PurchaseOrderCreatePage /></Guard>} />
          <Route path="purchase-orders/:id/edit" element={<Guard manageOnly><PurchaseOrderCreatePage /></Guard>} />
          <Route path="purchase-orders/:id"            element={<Guard denyDEO><PurchaseOrderDetailPage /></Guard>} />
          <Route path="stock-transfers"                element={<Guard manageOnly><StockTransferListPage /></Guard>} />
          <Route path="stock-transfers/new"            element={<Guard manageOnly><StockTransferCreatePage /></Guard>} />
          <Route path="products"                       element={<ProductListPage />} />
          <Route path="clients"               element={<ClientListPage />} />
          <Route path="admin/users"           element={<Guard adminOnly><AdminUsersPage /></Guard>} />
          <Route path="admin/settings"        element={<Guard adminOnly><AdminSettingsPage /></Guard>} />
        </Route>
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </Suspense>
  );
};

export default function App() {
  return (
    <ErrorBoundary shell><BrowserRouter>
      <AuthProvider>
        <ApiStatus />
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { fontFamily: 'Outfit, sans-serif', fontSize: '14px' },
          }}
        />
      </AuthProvider>
    </BrowserRouter></ErrorBoundary>
  );
}
